import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  useColorModeValue,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Badge,
  VStack,
  HStack,
  Icon,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Skeleton,
  Alert,
  AlertIcon,
  Button,
} from '@chakra-ui/react';
import {
  FaTasks,
  FaProjectDiagram,
  FaUsers,
  FaExclamationTriangle,
  FaDollarSign,
  FaChartLine,
} from 'react-icons/fa';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';
import { useTasks } from '../../hooks/useTasks';
import { useProjects } from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';
import { useUsers } from '../../hooks/useUsers';
import { exportAnalyticsCsv } from '../../api/analyticsApi';
import {
  CHART_PALETTE,
  PRIORITY_COLORS,
  STATUS_COLORS,
  barOptions,
  doughnutOptions,
  formatLabel,
  getId,
  horizontalBarOptions,
  isCompletedStatus,
  lineOptions,
  toDateKey,
} from './analyticsChartConfig';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Filler
);

const ChartPanel: React.FC<{
  title: string;
  subtitle?: string;
  icon: React.ElementType;
  iconColor: string;
  height?: string;
  children: React.ReactNode;
  cardBg: string;
  borderColor: string;
}> = ({ title, subtitle, icon, iconColor, height = '300px', children, cardBg, borderColor }) => (
  <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="sm" h="full">
    <CardHeader pb={2}>
      <HStack justify="space-between" align="start">
        <Box>
          <HStack spacing={2} mb={subtitle ? 1 : 0}>
            <Icon as={icon} color={iconColor} />
            <Heading size="sm">{title}</Heading>
          </HStack>
          {subtitle && (
            <Text fontSize="sm" color="gray.500">
              {subtitle}
            </Text>
          )}
        </Box>
      </HStack>
    </CardHeader>
    <CardBody pt={0}>
      <Box h={height} w="100%">
        {children}
      </Box>
    </CardBody>
  </Card>
);

const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  help: string;
  icon: React.ElementType;
  iconColor: string;
  cardBg: string;
  borderColor: string;
  valueColor?: string;
  progress?: number;
  progressColor?: string;
}> = ({
  label,
  value,
  help,
  icon,
  iconColor,
  cardBg,
  borderColor,
  valueColor,
  progress,
  progressColor = 'blue',
}) => (
  <Box bg={cardBg} p={5} borderRadius="xl" border="1px solid" borderColor={borderColor} boxShadow="sm" h="full">
    <Stat>
      <StatLabel>
        <HStack spacing={2}>
          <Flex w={9} h={9} borderRadius="lg" bg={`${iconColor.split('.')[0]}.50`} align="center" justify="center">
            <Icon as={icon} color={iconColor} />
          </Flex>
          <Text fontWeight="medium">{label}</Text>
        </HStack>
      </StatLabel>
      <StatNumber fontSize="3xl" color={valueColor} mt={3}>
        {value}
      </StatNumber>
      <StatHelpText mt={2}>{help}</StatHelpText>
    </Stat>
    {progress != null && (
      <Progress value={progress} colorScheme={progressColor} mt={4} borderRadius="full" size="sm" />
    )}
  </Box>
);

const AnalyticsTab: React.FC = () => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const mutedText = useColorModeValue('gray.600', 'gray.400');

  const { data: tasks = [], isLoading: tasksLoading, error: tasksError } = useTasks(true);
  const { data: projects = [], isLoading: projectsLoading, error: projectsError } = useProjects(true);
  const { data: clients = [], isLoading: clientsLoading, error: clientsError } = useClients();
  const { data: users = [], isLoading: usersLoading, error: usersError } = useUsers();

  const isLoading = tasksLoading || projectsLoading || clientsLoading || usersLoading;
  const hasError = tasksError || projectsError || clientsError || usersError;

  const analytics = useMemo(() => {
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter((task) => isCompletedStatus(task.status)).length;
    const activeTasks = totalTasks - completedTasks;

    const tasksByStatus = tasks.reduce<Record<string, number>>((acc, task) => {
      const key = task.status || 'Unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const tasksByPriority = tasks.reduce<Record<string, number>>((acc, task) => {
      const key = task.priority || 'Unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const tasksByType = tasks.reduce<Record<string, number>>((acc, task) => {
      const key = task.type || 'Unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const projectsByType = projects.reduce<Record<string, number>>((acc, project) => {
      const key = project.type || 'Unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const totalQuotedValue = tasks.reduce((sum, task) => sum + (task.quotedPrice || 0), 0);
    const completedTasksValue = tasks
      .filter((task) => isCompletedStatus(task.status))
      .reduce((sum, task) => sum + (task.agreedPrice || task.quotedPrice || 0), 0);

    const now = new Date();
    const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const tasksThisWeek = tasks.filter((task) => new Date(task.createdAt) > thisWeek).length;
    const tasksThisMonth = tasks.filter((task) => new Date(task.createdAt) > thisMonth).length;
    const completedThisWeek = tasks.filter(
      (task) => isCompletedStatus(task.status) && new Date(task.updatedAt) > thisWeek
    ).length;

    const overdueTasks = tasks.filter((task) => {
      if (!task.dueDate || isCompletedStatus(task.status)) return false;
      const dueDateEndOfDay = new Date(task.dueDate);
      dueDateEndOfDay.setHours(23, 59, 59, 999);
      const todayStartOfDay = new Date();
      todayStartOfDay.setHours(0, 0, 0, 0);
      return dueDateEndOfDay < todayStartOfDay;
    }).length;

    const projectClientMap = new Map<string, string>();
    projects.forEach((project) => {
      const clientId = getId(project.client as string | { _id: string } | null);
      if (clientId) projectClientMap.set(project._id, clientId);
    });

    const tasksByClient = clients
      .map((client) => {
        const count = tasks.filter((task) => {
          const projectId = getId(task.project as string | { _id: string } | undefined);
          return projectId && projectClientMap.get(projectId) === client._id;
        }).length;
        return { name: client.clientName, count };
      })
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    const tasksByProject = projects
      .map((project) => {
        const count = tasks.filter((task) => getId(task.project as string | { _id: string } | undefined) === project._id).length;
        return { name: project.projectName, count };
      })
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    const assignmentCounts = new Map<string, number>();
    tasks.forEach((task) => {
      (task.assignedToUser || []).forEach((assignee) => {
        const userId = getId(assignee as string | { _id: string });
        if (!userId) return;
        assignmentCounts.set(userId, (assignmentCounts.get(userId) || 0) + 1);
      });
    });

    const tasksByAssignee = users
      .map((user) => ({
        name: `${user.firstName} ${user.lastName}`,
        count: assignmentCounts.get(user._id) || 0,
      }))
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    const userProductivity = users
      .map((user) => {
        const assignedTasks = tasks.filter((task) =>
          (task.assignedToUser || []).some((assignee) => getId(assignee as string | { _id: string }) === user._id)
        );
        const authorizedTasks = tasks.filter((task) =>
          (task.authBy || []).some((ref) => getId(ref as string | { _id: string }) === user._id)
        );
        const relevantTasks = assignedTasks.length > 0 ? assignedTasks : authorizedTasks;
        const completedCount = relevantTasks.filter((task) => isCompletedStatus(task.status)).length;
        return {
          user,
          tasksCount: relevantTasks.length,
          completedTasks: completedCount,
        };
      })
      .filter((item) => item.tasksCount > 0)
      .sort((a, b) => b.completedTasks - a.completedTasks || b.tasksCount - a.tasksCount);

    const days = Array.from({ length: 14 }, (_, index) => {
      const day = new Date();
      day.setDate(day.getDate() - (13 - index));
      return day;
    });

    const tasksPerDay = days.map((day) => {
      const dayKey = toDateKey(day);
      return tasks.filter((task) => toDateKey(task.createdAt) === dayKey).length;
    });

    const completedPerDay = days.map((day) => {
      const dayKey = toDateKey(day);
      return tasks.filter(
        (task) => isCompletedStatus(task.status) && toDateKey(task.updatedAt) === dayKey
      ).length;
    });

    const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

    return {
      totalTasks,
      completedTasks,
      activeTasks,
      totalProjects: projects.length,
      totalClients: clients.length,
      totalUsers: users.length,
      tasksByStatus,
      tasksByPriority,
      tasksByType,
      projectsByType,
      totalQuotedValue,
      completedTasksValue,
      tasksThisWeek,
      tasksThisMonth,
      completedThisWeek,
      completionRate,
      overdueTasks,
      tasksByClient,
      tasksByProject,
      tasksByAssignee,
      userProductivity,
      days,
      tasksPerDay,
      completedPerDay,
      averageTasksPerProject: projects.length > 0 ? totalTasks / projects.length : 0,
    };
  }, [tasks, projects, clients, users]);

  const statusChartData = useMemo(() => {
    const labels = Object.keys(analytics.tasksByStatus);
    return {
      labels: labels.map(formatLabel),
      datasets: [
        {
          data: Object.values(analytics.tasksByStatus),
          backgroundColor: labels.map(
            (label) => STATUS_COLORS[label.toLowerCase()] || CHART_PALETTE[labels.indexOf(label) % CHART_PALETTE.length]
          ),
          borderWidth: 0,
          hoverOffset: 8,
        },
      ],
    };
  }, [analytics.tasksByStatus]);

  const priorityChartData = useMemo(() => {
    const labels = Object.keys(analytics.tasksByPriority);
    return {
      labels: labels.map(formatLabel),
      datasets: [
        {
          label: 'Tasks',
          data: Object.values(analytics.tasksByPriority),
          backgroundColor: labels.map(
            (label) => PRIORITY_COLORS[label.toLowerCase()] || CHART_PALETTE[labels.indexOf(label) % CHART_PALETTE.length]
          ),
          borderRadius: 8,
          maxBarThickness: 42,
        },
      ],
    };
  }, [analytics.tasksByPriority]);

  const activityChartData = useMemo(
    () => ({
      labels: analytics.days.map((day) =>
        day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      ),
      datasets: [
        {
          label: 'Created',
          data: analytics.tasksPerDay,
          borderColor: '#3182ce',
          backgroundColor: 'rgba(49, 130, 206, 0.12)',
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
        {
          label: 'Completed',
          data: analytics.completedPerDay,
          borderColor: '#38a169',
          backgroundColor: 'rgba(56, 161, 105, 0.08)',
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 5,
        },
      ],
    }),
    [analytics.days, analytics.tasksPerDay, analytics.completedPerDay]
  );

  const projectChartData = useMemo(
    () => ({
      labels: analytics.tasksByProject.map((item) => item.name),
      datasets: [
        {
          label: 'Tasks',
          data: analytics.tasksByProject.map((item) => item.count),
          backgroundColor: '#805ad5',
          borderRadius: 8,
          maxBarThickness: 28,
        },
      ],
    }),
    [analytics.tasksByProject]
  );

  const clientChartData = useMemo(
    () => ({
      labels: analytics.tasksByClient.map((item) => item.name),
      datasets: [
        {
          label: 'Tasks',
          data: analytics.tasksByClient.map((item) => item.count),
          backgroundColor: '#319795',
          borderRadius: 8,
          maxBarThickness: 28,
        },
      ],
    }),
    [analytics.tasksByClient]
  );

  const assigneeChartData = useMemo(
    () => ({
      labels: analytics.tasksByAssignee.map((item) => item.name),
      datasets: [
        {
          label: 'Assigned tasks',
          data: analytics.tasksByAssignee.map((item) => item.count),
          backgroundColor: '#ed8936',
          borderRadius: 8,
          maxBarThickness: 28,
        },
      ],
    }),
    [analytics.tasksByAssignee]
  );

  const typeChartData = useMemo(() => {
    const labels = Object.keys(analytics.tasksByType);
    return {
      labels: labels.map(formatLabel),
      datasets: [
        {
          label: 'Tasks',
          data: Object.values(analytics.tasksByType),
          backgroundColor: labels.map((_, index) => CHART_PALETTE[index % CHART_PALETTE.length]),
          borderRadius: 8,
          maxBarThickness: 42,
        },
      ],
    };
  }, [analytics.tasksByType]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(amount);

  if (isLoading) {
    return (
      <Box bg={pageBg} minH="100%" p={{ base: 4, md: 6 }}>
        <VStack spacing={6} align="stretch">
          <Skeleton height="36px" width="280px" />
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
            {[1, 2, 3, 4].map((item) => (
              <Skeleton key={item} height="140px" borderRadius="xl" />
            ))}
          </SimpleGrid>
          <Skeleton height="360px" borderRadius="xl" />
          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
            <Skeleton height="340px" borderRadius="xl" />
            <Skeleton height="340px" borderRadius="xl" />
          </SimpleGrid>
        </VStack>
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box bg={pageBg} minH="100%" p={{ base: 4, md: 6 }}>
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          Failed to load analytics data. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box bg={pageBg} minH="100%" py={{ base: 2, md: 4 }}>
      <Flex mb={8} justify="space-between" align="flex-start" wrap="wrap" gap={4}>
        <Box>
          <Heading size="lg" mb={2}>
            Analytics
          </Heading>
          <Text color={mutedText}>
            Insights across tasks, projects, clients, and team workload.
          </Text>
        </Box>
        <Button
          size="sm"
          colorScheme="blue"
          variant="outline"
          onClick={async () => {
            const blob = await exportAnalyticsCsv();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'tasks-export.csv';
            a.click();
            URL.revokeObjectURL(url);
          }}
        >
          Export CSV
        </Button>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4} mb={6}>
        <KpiCard
          label="Completion rate"
          value={`${analytics.completionRate.toFixed(1)}%`}
          help={`${analytics.completedTasks} of ${analytics.totalTasks} tasks completed`}
          icon={FaTasks}
          iconColor="blue.500"
          cardBg={cardBg}
          borderColor={borderColor}
          progress={analytics.completionRate}
          progressColor={analytics.completionRate > 75 ? 'green' : analytics.completionRate > 50 ? 'yellow' : 'red'}
        />
        <KpiCard
          label="Pipeline value"
          value={formatCurrency(analytics.totalQuotedValue)}
          help={`${formatCurrency(analytics.completedTasksValue)} realized`}
          icon={FaDollarSign}
          iconColor="green.500"
          cardBg={cardBg}
          borderColor={borderColor}
        />
        <KpiCard
          label="Overdue tasks"
          value={analytics.overdueTasks}
          help={analytics.overdueTasks > 0 ? 'Needs attention' : 'All on track'}
          icon={FaExclamationTriangle}
          iconColor="red.500"
          cardBg={cardBg}
          borderColor={borderColor}
          valueColor={analytics.overdueTasks > 0 ? 'red.500' : 'green.500'}
        />
        <KpiCard
          label="Weekly activity"
          value={analytics.tasksThisWeek}
          help={`${analytics.completedThisWeek} completed this week`}
          icon={FaChartLine}
          iconColor="purple.500"
          cardBg={cardBg}
          borderColor={borderColor}
        />
      </SimpleGrid>

      <Grid templateColumns={{ base: '1fr', xl: '2fr 1fr' }} gap={6} mb={6}>
        <GridItem>
          <ChartPanel
            title="Task activity"
            subtitle="Created vs completed over the last 14 days"
            icon={FaChartLine}
            iconColor="blue.500"
            height="320px"
            cardBg={cardBg}
            borderColor={borderColor}
          >
            <Line data={activityChartData} options={lineOptions} />
          </ChartPanel>
        </GridItem>
        <GridItem>
          <ChartPanel
            title="Status breakdown"
            subtitle="Current distribution of task statuses"
            icon={FaTasks}
            iconColor="blue.500"
            height="320px"
            cardBg={cardBg}
            borderColor={borderColor}
          >
            {Object.keys(analytics.tasksByStatus).length > 0 ? (
              <Doughnut data={statusChartData} options={doughnutOptions} />
            ) : (
              <Flex h="100%" align="center" justify="center" color="gray.400">
                No task status data yet
              </Flex>
            )}
          </ChartPanel>
        </GridItem>
      </Grid>

      <SimpleGrid columns={{ base: 1, lg: 2, xl: 3 }} spacing={6} mb={6}>
        <ChartPanel
          title="Priority mix"
          subtitle="Tasks grouped by priority"
          icon={FaExclamationTriangle}
          iconColor="orange.500"
          height="280px"
          cardBg={cardBg}
          borderColor={borderColor}
        >
          {Object.keys(analytics.tasksByPriority).length > 0 ? (
            <Bar data={priorityChartData} options={barOptions} />
          ) : (
            <Flex h="100%" align="center" justify="center" color="gray.400">
              No priority data yet
            </Flex>
          )}
        </ChartPanel>

        <ChartPanel
          title="Task types"
          subtitle="Work categories across the org"
          icon={FaTasks}
          iconColor="purple.500"
          height="280px"
          cardBg={cardBg}
          borderColor={borderColor}
        >
          {Object.keys(analytics.tasksByType).length > 0 ? (
            <Bar data={typeChartData} options={barOptions} />
          ) : (
            <Flex h="100%" align="center" justify="center" color="gray.400">
              No task type data yet
            </Flex>
          )}
        </ChartPanel>

        <ChartPanel
          title="Project types"
          subtitle={`${analytics.totalProjects} projects · ${analytics.averageTasksPerProject.toFixed(1)} tasks avg`}
          icon={FaProjectDiagram}
          iconColor="purple.500"
          height="280px"
          cardBg={cardBg}
          borderColor={borderColor}
        >
          {Object.keys(analytics.projectsByType).length > 0 ? (
            <Bar
              data={{
                labels: Object.keys(analytics.projectsByType).map(formatLabel),
                datasets: [
                  {
                    label: 'Projects',
                    data: Object.values(analytics.projectsByType),
                    backgroundColor: '#805ad5',
                    borderRadius: 8,
                    maxBarThickness: 42,
                  },
                ],
              }}
              options={barOptions}
            />
          ) : (
            <Flex h="100%" align="center" justify="center" color="gray.400">
              No project data yet
            </Flex>
          )}
        </ChartPanel>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={6} mb={6}>
        <ChartPanel
          title="Top projects by workload"
          subtitle="Projects with the most tasks"
          icon={FaProjectDiagram}
          iconColor="purple.500"
          height="320px"
          cardBg={cardBg}
          borderColor={borderColor}
        >
          {analytics.tasksByProject.length > 0 ? (
            <Bar data={projectChartData} options={horizontalBarOptions} />
          ) : (
            <Flex h="100%" align="center" justify="center" color="gray.400">
              No project task data yet
            </Flex>
          )}
        </ChartPanel>

        <ChartPanel
          title="Top clients by workload"
          subtitle="Clients linked through projects"
          icon={FaUsers}
          iconColor="teal.500"
          height="320px"
          cardBg={cardBg}
          borderColor={borderColor}
        >
          {analytics.tasksByClient.length > 0 ? (
            <Bar data={clientChartData} options={horizontalBarOptions} />
          ) : (
            <Flex h="100%" align="center" justify="center" color="gray.400">
              No client workload data yet
            </Flex>
          )}
        </ChartPanel>
      </SimpleGrid>

      <Grid templateColumns={{ base: '1fr', xl: '1.2fr 1fr' }} gap={6}>
        <GridItem>
          <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderRadius="xl" boxShadow="sm">
            <CardHeader>
              <HStack spacing={2}>
                <Icon as={FaUsers} color="teal.500" />
                <Heading size="sm">Team performance</Heading>
              </HStack>
              <Text fontSize="sm" color={mutedText} mt={1}>
                Assigned or authorized tasks with completion rates
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              {analytics.userProductivity.length > 0 ? (
                <Box overflowX="auto">
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>User</Th>
                        <Th isNumeric>Tasks</Th>
                        <Th isNumeric>Completed</Th>
                        <Th isNumeric>Rate</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {analytics.userProductivity.slice(0, 8).map((userStat) => {
                        const rate =
                          userStat.tasksCount > 0
                            ? (userStat.completedTasks / userStat.tasksCount) * 100
                            : 0;
                        return (
                          <Tr key={userStat.user._id}>
                            <Td fontWeight="medium">
                              {userStat.user.firstName} {userStat.user.lastName}
                            </Td>
                            <Td isNumeric>{userStat.tasksCount}</Td>
                            <Td isNumeric>{userStat.completedTasks}</Td>
                            <Td isNumeric>
                              <Badge
                                colorScheme={rate > 75 ? 'green' : rate > 50 ? 'yellow' : 'orange'}
                                borderRadius="full"
                              >
                                {userStat.tasksCount > 0 ? `${rate.toFixed(0)}%` : 'N/A'}
                              </Badge>
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color="gray.400" py={8} textAlign="center">
                  No team workload data yet
                </Text>
              )}
            </CardBody>
          </Card>
        </GridItem>

        <GridItem>
          <ChartPanel
            title="Assignments by user"
            subtitle="Open workload across the team"
            icon={FaUsers}
            iconColor="orange.500"
            height="320px"
            cardBg={cardBg}
            borderColor={borderColor}
          >
            {analytics.tasksByAssignee.length > 0 ? (
              <Bar data={assigneeChartData} options={horizontalBarOptions} />
            ) : (
              <Flex h="100%" align="center" justify="center" color="gray.400">
                No assignment data yet
              </Flex>
            )}
          </ChartPanel>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default AnalyticsTab;
